import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { GraduationCap, ChevronRight, Mail } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SEO from '../components/SEO';
import LoadingScreen from '../components/LoadingScreen'; // <--- IMPORT STANDARD

const DepartmentDetails = () => {
  const { deptId } = useParams();
  const [dept, setDept] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 1. Fetch Info Dipartimento
        const { data: deptData, error: deptError } = await supabase
          .from('departments')
          .select('*')
          .eq('id', deptId)
          .single();
        
        if (deptError) throw deptError;
        setDept(deptData);

        // 2. Fetch Corsi
        const { data: courseData, error: courseError } = await supabase
          .from('courses')
          .select('*')
          .eq('department_id', deptId)
          .order('name');
        
        if (courseError) throw courseError;
        setCourses(courseData || []);

      } catch (error) {
        console.error("Errore:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [deptId]);

  // --- LOADING SCREEN STANDARD ---
  if (loading) return <LoadingScreen />;
  
  if (!dept) return <div className="p-20 text-center text-slate-500 font-bold">Dipartimento non trovato.</div>;

  // Filtra i corsi per tipo
  const triennali = courses.filter(c => c.type === 'triennale');
  const magistrali = courses.filter(c => c.type === 'magistrale' || c.type === 'ciclo_unico');

  // Componente Card Corso
  const CourseCard = ({ course }) => (
    <Link to={`/course/${deptId}/${course.id}`} className="group relative bg-white border border-slate-100 p-6 rounded-[2rem] hover:shadow-xl hover:border-indigo-200 transition-all flex items-center justify-between hover:-translate-y-1 duration-300">
        <div className="flex items-center gap-5">
            <div className="bg-indigo-50 p-4 rounded-2xl text-indigo-600 group-hover:scale-110 transition-all shadow-sm group-hover:bg-indigo-600 group-hover:text-white">
                <GraduationCap size={28} />
            </div>
            <div>
                <h3 className="text-lg font-bold text-slate-800 group-hover:text-indigo-700 transition-colors leading-tight">
                    {course.name.replace(/\[.*?\]\s*/, '')}
                </h3>
                <span className="inline-block mt-1.5 text-[10px] font-bold text-slate-500 bg-slate-50 px-2 py-1 rounded-lg border border-slate-100 uppercase tracking-wider">
                    {course.type}
                </span>
            </div>
        </div>
        <div className="text-slate-300 group-hover:text-indigo-600 group-hover:translate-x-1 transition-all bg-slate-50 p-2 rounded-full">
            <ChevronRight size={20} />
        </div>
    </Link>
  );

  return (
    <div className="relative min-h-screen bg-slate-50 pt-24 pb-20 px-6 font-sans text-slate-800">
      <SEO title={`${dept.acronym} - Corsi`} description={`Corsi di laurea del dipartimento ${dept.acronym}.`} url={`/dept/${deptId}`} />
      
      {/* Sfondo Decorativo */}
      <div className="absolute top-0 left-[-10%] w-[500px] h-[500px] bg-indigo-100/60 rounded-full blur-[100px] mix-blend-multiply pointer-events-none"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto">
        <PageHeader 
            title={dept.name} 
            subtitle="Seleziona il tuo corso di laurea per vedere il materiale." 
            breadcrumbs={[{label: 'Dipartimenti', link: '/dipartimenti'}, {label: dept.acronym}]} 
        />

        {courses.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-indigo-200 rounded-[2.5rem] p-12 text-center mt-10">
                <Mail className="h-10 w-10 text-indigo-600 mx-auto mb-4" />
                <h3 className="text-2xl font-black text-slate-900 mb-2">Dipartimento in costruzione 🚧</h3>
                <p className="text-slate-500">Stiamo caricando i corsi per il {dept.acronym}.</p>
            </div>
        ) : (
            <div className="space-y-12 mt-10">
                {triennali.length > 0 && (
                    <div>
                        <h3 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-3">
                            <span className="w-2 h-8 bg-indigo-500 rounded-full"></span> Lauree Triennali
                        </h3>
                        <div className="grid md:grid-cols-2 gap-4">
                            {triennali.map(course => <CourseCard key={course.id} course={course} />)}
                        </div>
                    </div>
                )}
                {magistrali.length > 0 && (
                    <div>
                        <h3 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-3">
                            <span className="w-2 h-8 bg-fuchsia-500 rounded-full"></span> Lauree Magistrali
                        </h3>
                        <div className="grid md:grid-cols-2 gap-4">
                            {magistrali.map(course => <CourseCard key={course.id} course={course} />)}
                        </div>
                    </div>
                )}
            </div>
        )}
      </div>
    </div>
  );
};

export default DepartmentDetails;